import type { Observable, Subscription } from "rxjs";
import { timer as observableTimer } from "rxjs";

import type { IComponentController, IScope } from "angular";
import { filter, forEach, forOwn } from "lodash";
import type {
    IEnvironmentConfiguration,
    IResponseWithLoading,
    ISite,
    ISiteEquipmentMonitoringResult,
    ISiteSlim,
    IVideoStream,
} from "App/Modules/Models";
import { EquipmentClass, ImageUpdateStatus, SiteState, FunctionalityType } from "App/Modules/Models";
import type {
    IEnvironmentConfigurationRepository,
    ISiteEquipmentRepository,
    ISitesRepository,
} from "App/Modules/Repositories";
import type { IPageService } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";
import { finalize, mergeMap } from "rxjs/operators";

export default class SystemsTestingComponentController implements IComponentController {
    public static $inject: string[] = [
        "$scope",
        "irPageService",
        "mmEnvironmentConfigurationRepository",
        "mmSitesRepository",
        "mmSiteEquipmentRepository",
    ];

    public readonly HOME_POSITION = "home";

    public readonly CARE_POSITION = "care";

    public clientAdministrationToolUrl: string;

    public equipmentList: IResponseWithLoading<ISiteEquipmentMonitoringResult[]>;

    public isWaitingForNewImage: boolean;

    public requestImageUploadResponse: IResponseWithLoading<void>;

    public site: IResponseWithLoading<ISiteSlim>;

    public rotationToCareRequests: any = {};

    public rotationToHomeRequests: any = {};

    public testSiteEquipmentResponses: {
        [id: string]: IResponseWithLoading<void>;
    };

    public siteId: string;

    public intervalSubscription: Subscription;

    public warnings: { [key: string]: number };

    public lastCoveragesRequestDate: Date;

    public collapseDiagnostics = true;

    public imageWasRequested = false;

    constructor(
        private readonly $scope: IScope,
        private readonly pageService: IPageService,
        private readonly configurationRepository: IEnvironmentConfigurationRepository,
        private readonly sitesRepository: ISitesRepository,
        private readonly siteEquipmentRepository: ISiteEquipmentRepository,
    ) {
        this.testSiteEquipmentResponses = {};
    }

    public $onInit() {
        if (this.siteId) {
            extendWithLoadingAndFail(this.sitesRepository.getSiteSlim(this.siteId))
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (result: IResponseWithLoading<ISiteSlim>) => {
                        this.site = result;
                    },
                    (result: IResponseWithLoading<ISite>) => {
                        this.site = result;
                    },
                );

            this.intervalSubscription = this.polling()
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (response) => {
                        this.lastCoveragesRequestDate = new Date();
                        if (!response.isLoading && !response.error) {
                            this.equipmentList = response;

                            this.getWarnings(response.data);
                            this.isWaitingForNewImage = false;
                        }
                    },
                    (error) => {
                        this.equipmentList = error;
                    },
                );
        }

        extendWithLoadingAndFail(this.configurationRepository.getConfiguration())
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response: IResponseWithLoading<IEnvironmentConfiguration>) => {
                    if (response.data) {
                        this.clientAdministrationToolUrl = `${response.data.clientToolsInstallersDownloadPath}IRISClientManager.exe`;
                    }
                },
                () => {
                    this.clientAdministrationToolUrl = "";
                },
            );
    }

    public $onDestroy() {
        this.intervalSubscription.unsubscribe();
    }

    public requestImageUpload() {
        this.isWaitingForNewImage = true;
        this.imageWasRequested = true;

        extendWithLoadingAndFail(this.sitesRepository.requestNewImageUpload(this.siteId))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    this.requestImageUploadResponse = response;
                },
                (error) => {
                    this.requestImageUploadResponse = error;
                },
            );
    }

    public requestTestSiteEquipment(siteEquipmentId: string) {
        extendWithLoadingAndFail(this.siteEquipmentRepository.requestTestOfSiteEquipment(this.siteId, siteEquipmentId))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    this.testSiteEquipmentResponses[siteEquipmentId] = response;
                },
                (error) => {
                    this.testSiteEquipmentResponses[siteEquipmentId] = error;
                },
            );
    }

    public rotateCamera(equipmentId: string, position: string) {
        const responses = position === this.HOME_POSITION ? this.rotationToHomeRequests : this.rotationToCareRequests;

        extendWithLoadingAndFail(this.sitesRepository.rotateCameraOnSite(this.siteId, equipmentId, position))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    responses[equipmentId] = response;
                },
                (error) => {
                    responses[equipmentId] = error;
                },
            );
    }

    public getCameraEquipment(): ISiteEquipmentMonitoringResult[] {
        if (!this.equipmentList) {
            return null;
        }
        return filter(
            this.equipmentList.data,
            (equipment) =>
                equipment.equipmentClass === EquipmentClass.Camera ||
                equipment.equipmentClass === EquipmentClass.AnalogCamera,
        );
    }

    public hasViewCoverages(equipment: ISiteEquipmentMonitoringResult): boolean {
        return equipment.videoStreams?.some((videoStream) => videoStream.viewCoverages.length > 0);
    }

    public getOtherEquipment(): ISiteEquipmentMonitoringResult[] {
        if (!this.equipmentList) {
            return null;
        }
        return filter(
            this.equipmentList.data,
            (equipment) =>
                equipment.equipmentClass !== EquipmentClass.Camera &&
                equipment.equipmentClass !== EquipmentClass.AnalogCamera,
        );
    }

    public hasCareFunctionality(): boolean {
        if (this.site.data) {
            return this.site.data.functionalityTypes.includes(FunctionalityType.Care);
        }
        return false;
    }

    public isTestsAvailable(): boolean {
        if (this.hasCareFunctionality()) {
            return (
                this.site.data.siteState === SiteState.NotStarted || this.site.data.siteState === SiteState.Maintenance
            );
        }
        return true;
    }

    public getWarnings(equipmentList: ISiteEquipmentMonitoringResult[]) {
        const warnings: { [key: string]: number } = {};

        let notConnectedCount = 0;
        let noHeartbeatCount = 0;
        let noImageCount = 0;
        let wrongIpConfigurationCount = 0;

        forEach(equipmentList, (equipment) => {
            if (!equipment.isEquipmentConnected) {
                notConnectedCount++;
            } else if (equipment.hasWrongIpConfiguration) {
                wrongIpConfigurationCount++;
            } else {
                if (!equipment.dtLastHeartBeat) {
                    noHeartbeatCount++;
                }

                forEach(equipment.videoStreams, (vs) => {
                    if (!vs.dtLastImageUpdate) {
                        noImageCount++;
                    }
                });
            }
        });

        warnings.NotConnected = notConnectedCount;
        warnings.NoHeartbeat = noHeartbeatCount;
        warnings.NoImageUpdate = noImageCount;
        warnings.WrongIpConfiguration = wrongIpConfigurationCount;

        this.warnings = warnings;
    }

    public showGuide() {
        let anyWarnings = false;

        forOwn(this.warnings, (warningCount) => {
            anyWarnings = anyWarnings || warningCount > 0;
        });

        return anyWarnings;
    }

    public imageExists(videoStream: IVideoStream) {
        return videoStream.images.latest.url && videoStream.latestImageUpdateStatus !== ImageUpdateStatus.Error;
    }

    private polling(): Observable<IResponseWithLoading<ISiteEquipmentMonitoringResult[]>> {
        return observableTimer(0, 60 * 1000).pipe(
            mergeMap(() => extendWithLoadingAndFail(this.sitesRepository.getEquipmentMonitoringForSite(this.siteId))),
        );
    }
}
